'use client'

import type { MediaGalleryFieldsFragment } from '@/graphql/types'
import { useContentfulEntryLivePreview } from '@/components/providers/useContentfulEntryLivePreview'
import { DynamicMediaGallery } from '@ccpgames/eve-web-core'
import { useDlpTheme } from '@/components/providers/DlpThemeProvider'
import { toLibraryDlpTheme } from '@/lib/dlp-theme'
import { mapMediaBlocksToItems } from '../utils'
import { buildContentfulInspectorProps } from '@/components/providers/contentfulInspectorProps'

interface MediaGallerySlidesProps {
  entry: MediaGalleryFieldsFragment
}

/**
 * Slides view: Uses the library DynamicMediaGallery implementation.
 */
export function MediaGallerySlides({ entry }: MediaGallerySlidesProps) {
  const { updatedEntry, inspectorProps } = useContentfulEntryLivePreview(entry)
  const pageTheme = useDlpTheme()
  const theme = toLibraryDlpTheme(pageTheme)

  const view = 'slides' as const

  const items = mapMediaBlocksToItems(updatedEntry.contentCollection, {
    view,
    objectFit: 'contain',
    fill: false,
    getItemSectionInspectorProps: (mediaBlock) => buildContentfulInspectorProps(mediaBlock.sys.id, 'media'),
  })

  if (items.length === 0) {
    return null
  }

  // Container needs to be injected into the contentWrapper prop so the slides background image is full width

  return (
    <DynamicMediaGallery
      view={view}
      title={updatedEntry.title ?? undefined}
      content={updatedEntry.subtitle ?? undefined}
      items={items}
      theme={theme}
      sectionInspectorProps={inspectorProps('contentCollection')}
    />
  )
}
